import React from "react";
import { COLOR } from "../constants/colors";

interface Slot {
  time: string;
  booked: number;
  capacity: number;
  day?: string;
}

function availabilityLabel(booked: number, capacity: number) {
  if (booked >= capacity) return "Full";
  if (capacity - booked === 1) return `1 slot left`;
  return `${Math.max(capacity - booked, 0)} slots left`;
}

function barColor(booked: number, capacity: number) {
  if (booked >= capacity) return COLOR.subtext;
  const remaining = capacity - booked;
  const percentLeft = (remaining / capacity) * 100;
  if (remaining <= 3 || percentLeft <= 34) return COLOR.accent;
  return COLOR.subtext;
}

function TimeSlotInner({
  slot,
  selected,
  onSelect,
}: {
  slot: Slot;
  selected: boolean;
  onSelect: () => void;
}) {
  const percent = (slot.booked / slot.capacity) * 100;
  const isFull = slot.booked >= slot.capacity;
  const color = barColor(slot.booked, slot.capacity);
  return (
    <button
      onClick={onSelect}
      disabled={isFull}
      aria-pressed={selected}
      aria-label={`${slot.time}, ${availabilityLabel(
        slot.booked,
        slot.capacity
      )}`}
      className="flex-shrink-0 w-[120px] h-[64px] flex flex-col justify-center items-center rounded-2xl border p-2 transition focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{
        backgroundColor: selected ? COLOR.main : isFull ? COLOR.bg : COLOR.bg,
        borderColor: selected ? COLOR.mainBorder : COLOR.border,
        color: selected ? COLOR.bg : isFull ? COLOR.subtext : COLOR.text,
        borderWidth: "2px",
        cursor: isFull ? "not-allowed" : "pointer",
        opacity: isFull ? 0.6 : 1,
      }}
    >
      <span
        className="font-extrabold text-sm"
        style={{
          color: isFull ? COLOR.subtext : selected ? COLOR.bg : COLOR.text,
        }}
      >
        {slot.time}
      </span>
      <span
        className="text-xs"
        style={{ color: selected ? COLOR.bg : COLOR.subtext }}
      >
        {availabilityLabel(slot.booked, slot.capacity)}
      </span>
      <div
        className="mt-1 h-1 w-full rounded-full"
        style={{ backgroundColor: COLOR.border }}
      >
        <div
          className="h-1 rounded-full transition-all"
          style={{
            width: isFull ? "100%" : `${percent}%`,
            backgroundColor: isFull ? COLOR.border : color,
          }}
        />
      </div>
    </button>
  );
}

const TimeSlot = React.memo(TimeSlotInner);
export default TimeSlot;
